import React from 'react';
import { Box, Button, ButtonBase, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ContentPasteRounded as PasteIcon,
  KeyboardRounded as TypeIcon,
  MicRounded as MicIcon,
} from '@mui/icons-material';
import colors from '../../../assets/theme/colors';

const MODES = [
  {
    id: 'type',
    label: 'Type',
    icon: TypeIcon,
    title: 'Type a quick note.',
    text: "Jot down one observation or a tiny update. We'll keep it moving.",
    accent: colors.semantic.profileFlow.completionText,
    bg: alpha(colors.semantic.profileFlow.completionBg, 0.7),
  },
  {
    id: 'paste',
    label: 'Paste',
    icon: PasteIcon,
    title: 'Paste the messy stuff.',
    text: "Drop in a school note, message, or longer history. We'll pull the useful care details into place.",
    accent: colors.brand.deep,
    bg: alpha(colors.brand.ice, 0.9),
  },
  {
    id: 'dictate',
    label: 'Dictate',
    icon: MicIcon,
    title: "Just talk — we'll do the rest.",
    text: "Dictate anything — a messy ramble, a quick observation, a whole day. We'll capture it and sort it into your care log automatically.",
    accent: colors.semantic.profileFlow.completionText,
    bg: colors.semantic.profileFlow.completionText,
    featured: true,
  },
];

const getAnchorPlacement = (anchorRect) => {
  if (typeof window === 'undefined' || !anchorRect) {
    return {
      left: 0,
      top: 220,
      width: '100vw',
      pointerLeft: '50%',
    };
  }

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const isNarrow = viewportWidth < 760;
  const width = isNarrow ? viewportWidth : Math.min(680, viewportWidth - 64);
  const anchorCenter = anchorRect.left + (anchorRect.width / 2);
  const left = isNarrow
    ? 0
    : Math.min(Math.max(32, anchorCenter - (width / 2)), viewportWidth - width - 32);
  const top = Math.min(anchorRect.bottom + 28, Math.max(96, viewportHeight - 430));
  const pointerLeft = Math.min(Math.max(anchorCenter - left, 28), width - 28);

  return {
    left,
    top: Math.max(96, top),
    width,
    pointerLeft,
  };
};

const ModeTile = ({ mode, selected, onSelect }) => {
  const Icon = mode.icon;
  const selectedBg = mode.featured ? mode.bg : alpha(mode.accent, 0.08);
  const selectedColor = mode.featured ? '#FFFFFF' : mode.accent;

  return (
    <ButtonBase
      onClick={() => onSelect(mode.id)}
      sx={{
        minWidth: 0,
        minHeight: { xs: 118, sm: 132 },
        borderRadius: '22px',
        border: `2px solid ${selected ? alpha(mode.accent, 0.62) : alpha(mode.accent, 0.22)}`,
        bgcolor: selected ? selectedBg : alpha(colors.semantic.profileFlow.cardBg, 0.8),
        color: selected ? selectedColor : mode.accent,
        display: 'grid',
        placeItems: 'center',
        px: 0.9,
        py: 1.1,
        textAlign: 'center',
        boxShadow: selected && mode.featured ? `0 14px 26px ${alpha(mode.accent, 0.26)}` : 'none',
        '&:hover': {
          bgcolor: selected ? selectedBg : alpha(mode.accent, 0.1),
        },
      }}
    >
      <Box>
        <Box
          sx={{
            width: 54,
            height: 54,
            mx: 'auto',
            mb: 0.95,
            borderRadius: '17px',
            display: 'grid',
            placeItems: 'center',
            bgcolor: selected && mode.featured
              ? alpha('#FFFFFF', 0.18)
              : alpha(colors.semantic.profileFlow.cardBg, 0.92),
            color: 'inherit',
          }}
        >
          <Icon sx={{ fontSize: 27 }} />
        </Box>
        <Typography sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, fontWeight: 950, lineHeight: 1 }}>
          {mode.label}
        </Typography>
      </Box>
    </ButtonBase>
  );
};

const DashboardInputCoachMark = ({
  open,
  anchorRef,
  onDismiss,
}) => {
  const [activeMode, setActiveMode] = React.useState('dictate');
  const [anchorRect, setAnchorRect] = React.useState(null);

  React.useEffect(() => {
    if (!open) return undefined;

    const updateAnchorRect = () => {
      const nextRect = anchorRef?.current?.getBoundingClientRect?.();
      if (nextRect) {
        setAnchorRect(nextRect);
      }
    };

    updateAnchorRect();
    const frame = window.requestAnimationFrame(updateAnchorRect);
    window.addEventListener('resize', updateAnchorRect);
    window.addEventListener('scroll', updateAnchorRect, true);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', updateAnchorRect);
      window.removeEventListener('scroll', updateAnchorRect, true);
    };
  }, [anchorRef, open]);

  React.useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onDismiss?.();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onDismiss, open]);

  if (!open) return null;

  const active = MODES.find((mode) => mode.id === activeMode) || MODES[2];
  const placement = getAnchorPlacement(anchorRect);

  return (
    <>
      <Box
        onClick={onDismiss}
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 1300,
          bgcolor: alpha(colors.semantic.ink, 0.68),
          backdropFilter: 'blur(1px)',
        }}
      />
      <Box
        role="dialog"
        aria-modal="true"
        aria-label="Type paste or dictate coach mark"
        sx={{
          position: 'fixed',
          zIndex: 1303,
          top: placement.top,
          left: placement.left,
          width: placement.width,
          maxWidth: '100vw',
          px: { xs: 2, sm: 0 },
          boxSizing: 'border-box',
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            top: -17,
            left: placement.pointerLeft,
            width: 34,
            height: 34,
            bgcolor: colors.semantic.profileFlow.cardBg,
            transform: 'translateX(-50%) rotate(45deg)',
            borderTopLeftRadius: 3,
          }}
        />
        <Box
          sx={{
            position: 'relative',
            p: { xs: 2, sm: 2.4 },
            borderRadius: { xs: '26px', sm: '28px' },
            bgcolor: colors.semantic.profileFlow.cardBg,
            boxShadow: `0 26px 60px ${alpha(colors.semantic.ink, 0.3)}`,
          }}
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: { xs: 0.8, sm: 1 } }}>
            {MODES.map((mode) => (
              <ModeTile
                key={mode.id}
                mode={mode}
                selected={mode.id === activeMode}
                onSelect={setActiveMode}
              />
            ))}
          </Box>

          <Typography sx={{ mt: 2.25, fontSize: { xs: '1.34rem', sm: '1.55rem' }, lineHeight: 1.15, fontWeight: 950, color: colors.semantic.ink }}>
            {active.title}
          </Typography>
          <Typography sx={{ mt: 0.8, fontSize: { xs: '1rem', sm: '1.08rem' }, lineHeight: 1.45, color: alpha(colors.semantic.ink, 0.74) }}>
            {active.text}
          </Typography>

          <Box sx={{ mt: 2.35, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.2 }}>
            <Button
              variant="text"
              onClick={onDismiss}
              sx={{
                textTransform: 'none',
                px: 0,
                fontSize: '1rem',
                fontWeight: 850,
                color: alpha(colors.semantic.ink, 0.42),
                '&:hover': { bgcolor: 'transparent', color: alpha(colors.semantic.ink, 0.62) },
              }}
            >
              Skip
            </Button>
            <Button
              variant="contained"
              onClick={onDismiss}
              sx={{
                minWidth: 130,
                minHeight: 58,
                borderRadius: '22px',
                textTransform: 'none',
                fontSize: '1.08rem',
                fontWeight: 950,
                bgcolor: colors.semantic.profileFlow.completionText,
                color: '#FFFFFF',
                boxShadow: `0 18px 34px ${alpha(colors.semantic.profileFlow.completionText, 0.25)}`,
                '&:hover': {
                  bgcolor: colors.brand.navy,
                  boxShadow: `0 20px 38px ${alpha(colors.semantic.profileFlow.completionText, 0.3)}`,
                },
              }}
            >
              Got it
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default DashboardInputCoachMark;
